import { useQueryClient } from "@tanstack/react-query";

import {
  type AgendaItem,
  type AgendaItemList,
  type Encounter,
  getGetEncounterByIdQueryKey,
  getListActionItemsForEncounterQueryKey,
  getListAgendaItemsQueryKey,
  getListMedicalCodesByEncounterQueryKey,
} from "@elation/api-client/src/stitch-web";

import { useAppBusEvent } from "@src/features/sse";

import { matchesActiveEncounter } from "../domain";
import {
  useEncounterId,
  useIsEncounterCompleted,
  useIsEncounterFinalizing,
} from "./useEncounterActorSelectors";

type CachedListAgendaItems = { response?: unknown; data: AgendaItemList };
type CachedEncounter = { response?: unknown; data: Encounter };

export function useEncounter() {
  const queryClient = useQueryClient();

  const encounterId = useEncounterId();
  const isEncounterCompleted = useIsEncounterCompleted();
  const isEncounterFinalizing = useIsEncounterFinalizing();

  useAppBusEvent("encounter.snapshot", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({ ...old, data: { items: payload.items } }),
    );
  });

  useAppBusEvent("agenda_item.created", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => {
        const items: AgendaItem[] = old?.data?.items ?? [];
        if (items.some((i) => i.id === payload.item.id)) return old;
        return { ...old, data: { items: [...items, payload.item] } };
      },
    );
  });

  useAppBusEvent("agenda_item.updated", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({
        ...old,
        data: {
          items: (old?.data?.items ?? []).map((i: AgendaItem) =>
            i.id === payload.item.id ? payload.item : i,
          ),
        },
      }),
    );
  });

  useAppBusEvent("agenda_item.deleted", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({
        ...old,
        data: {
          items: (old?.data?.items ?? []).filter(
            (i: AgendaItem) => i.id !== payload.itemId,
          ),
        },
      }),
    );
  });

  useAppBusEvent("encounter.finalizing", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "finalizing" } }
          : old,
    );
  });

  useAppBusEvent("encounter.completed", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "completed" } }
          : old,
    );

    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetEncounterByIdQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListActionItemsForEncounterQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListMedicalCodesByEncounterQueryKey(payload.encounterId),
      }),
    ]).catch((err) =>
      console.error(
        "[useEncounter] Failed to invalidate queries after encounter.completed",
        {
          encounterId: payload.encounterId,
          err,
        },
      ),
    );
  });

  useAppBusEvent("encounter.coding_updated", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient
      .invalidateQueries({
        queryKey: getListMedicalCodesByEncounterQueryKey(payload.encounterId),
      })
      .catch((err) =>
        console.error(
          "[useEncounter] Failed to invalidate medical codes after encounter.coding_updated",
          { encounterId: payload.encounterId, err },
        ),
      );
  });

  useAppBusEvent("encounter.reopened", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "in_progress" } }
          : old,
    );

    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetEncounterByIdQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListAgendaItemsQueryKey(payload.encounterId),
      }),
    ]).catch((err) =>
      console.error(
        "[useEncounter] Failed to invalidate queries after encounter.reopened",
        {
          encounterId: payload.encounterId,
          err,
        },
      ),
    );
  });

  return {
    encounterId,
    isEncounterCompleted,
    isEncounterFinalizing,
  };
}
